/**
 * /admin/isolation-leads — table des leads "Isolation Combles + PAC à 1€".
 * Style cohérent avec AdminCEELeads.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
    Loader2, ShieldAlert, ChevronRight, Mail, Phone, MapPin, Calendar, Home,
    Thermometer, Trash2, ThermometerSun, Snowflake,
} from "lucide-react";


const RFR_LABEL = {
    lt_20k: "< 20 000 €",
    "20k_40k": "20 000 – 40 000 €",
    "40k_60k": "40 000 – 60 000 €",
    gt_60k: "> 60 000 €",
    unknown: "Non renseigné",
};

const STATUT_LABEL = {
    proprio_occupant: "Propriétaire occupant",
    proprio_bailleur: "Propriétaire bailleur",
    locataire: "Locataire",
};

const TRAVAUX_LABEL = {
    isolation: "Isolation combles",
    pac: "Pompe à chaleur",
    both: "Isolation + PAC",
};

const TRAVAUX_ICON = { isolation: Home, pac: Snowflake, both: ThermometerSun };


export default function AdminIsolationLeads() {
    const { user, loading: authLoading } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/isolation-pac/leads");
            setItems(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.role === "admin") load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const onDelete = async () => {
        if (!selected) return;
        if (!confirm("Supprimer définitivement ce lead ?")) return;
        setDeleting(true);
        try {
            await api.delete(`/admin/isolation-pac/leads/${selected.id}`);
            toast.success("Lead supprimé");
            setSelected(null);
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setDeleting(false);
        }
    };

    if (authLoading) return <div className="em-container py-32 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" /></div>;
    if (!user || user.role !== "admin") {
        return (
            <div className="em-container py-20" data-testid="admin-forbidden">
                <ShieldAlert className="h-12 w-12 text-destructive" />
                <h1 className="font-display text-4xl font-black text-primary mt-4">Accès refusé</h1>
                <Button asChild className="rounded-full mt-6 bg-primary hover:bg-primary/90"><Link to="/">Retour</Link></Button>
            </div>
        );
    }

    const total = items.length;
    const byTravaux = items.reduce((acc, l) => ({ ...acc, [l.travaux_souhaites]: (acc[l.travaux_souhaites] || 0) + 1 }), {});

    return (
        <div className="em-container py-8" data-testid="admin-isolation-page">
            <p className="em-eyebrow flex items-center gap-2"><Home className="h-3.5 w-3.5" />Administration</p>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-primary mt-2">Leads Isolation & PAC à 1€</h1>
            <p className="text-muted-foreground mt-3 max-w-2xl">Particuliers ayant testé leur éligibilité aux aides État (CEE + MaPrimeRénov').</p>

            {/* STATS */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-10">
                <StatCard icon={Home} label="Total" value={total} />
                <StatCard icon={ThermometerSun} label="Isolation seule" value={byTravaux.isolation || 0} />
                <StatCard icon={Snowflake} label="PAC seule" value={byTravaux.pac || 0} />
                <StatCard icon={Thermometer} label="Isolation + PAC" value={byTravaux.both || 0} />
            </div>

            {/* TABLE */}
            <Card className="rounded-3xl border-border mt-8">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="py-20 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" /></div>
                    ) : items.length === 0 ? (
                        <div className="py-16 text-center text-muted-foreground">
                            <Home className="h-12 w-12 mx-auto text-primary/30" />
                            <p className="mt-3">Aucun lead pour l'instant.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[860px]">
                                <thead className="bg-secondary">
                                    <tr className="text-left">
                                        <th className="px-5 py-4 em-eyebrow">Date</th>
                                        <th className="px-5 py-4 em-eyebrow">Contact</th>
                                        <th className="px-5 py-4 em-eyebrow">Statut</th>
                                        <th className="px-5 py-4 em-eyebrow">Logement</th>
                                        <th className="px-5 py-4 em-eyebrow">Revenu fiscal</th>
                                        <th className="px-5 py-4 em-eyebrow">Travaux</th>
                                        <th className="px-5 py-4"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((l) => {
                                        const Icon = TRAVAUX_ICON[l.travaux_souhaites] || Home;
                                        return (
                                            <tr key={l.id} className="border-t border-border hover:bg-muted/40">
                                                <td className="px-5 py-4 text-sm">{new Date(l.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}</td>
                                                <td className="px-5 py-4">
                                                    <p className="font-display font-bold">{l.full_name}</p>
                                                    <p className="text-xs text-muted-foreground">{l.email}</p>
                                                </td>
                                                <td className="px-5 py-4 text-sm">{STATUT_LABEL[l.statut] || l.statut}</td>
                                                <td className="px-5 py-4 text-sm">
                                                    {l.type_logement}{l.surface_m2 ? ` · ${l.surface_m2} m²` : ""}
                                                </td>
                                                <td className="px-5 py-4 text-sm font-mono">{RFR_LABEL[l.revenu_fiscal_bracket] || "—"}</td>
                                                <td className="px-5 py-4">
                                                    <span className="inline-flex items-center gap-1.5 bg-secondary px-2.5 py-1 rounded-full text-xs">
                                                        <Icon className="h-3 w-3" />
                                                        {TRAVAUX_LABEL[l.travaux_souhaites] || l.travaux_souhaites}
                                                    </span>
                                                </td>
                                                <td className="px-5 py-4 text-right">
                                                    <Button variant="ghost" size="sm" onClick={() => setSelected(l)} className="rounded-full">
                                                        Détail <ChevronRight className="h-4 w-4 ml-1" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* DETAIL */}
            <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">Détail du lead</DialogTitle>
                    </DialogHeader>
                    {selected && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <DetailField icon={Mail}    label="Email"           value={selected.email} />
                                <DetailField icon={Phone}   label="Téléphone"       value={selected.phone} />
                                <DetailField icon={MapPin}  label="Code postal"     value={selected.postal_code} />
                                <DetailField icon={Calendar} label="Reçu le"        value={new Date(selected.created_at).toLocaleString("fr-FR")} />
                                <DetailField icon={Home}    label="Statut"          value={STATUT_LABEL[selected.statut]} />
                                <DetailField icon={Home}    label="Type logement"   value={selected.type_logement} />
                                <DetailField icon={Home}    label="Surface"         value={selected.surface_m2 ? `${selected.surface_m2} m²` : "—"} />
                                <DetailField icon={Home}    label="Année construction" value={selected.annee_construction} />
                                <DetailField icon={Home}    label="Type combles"    value={selected.type_combles} />
                                <DetailField icon={Home}    label="Revenu fiscal"   value={RFR_LABEL[selected.revenu_fiscal_bracket]} />
                                <DetailField icon={ThermometerSun} label="Travaux souhaités" value={TRAVAUX_LABEL[selected.travaux_souhaites]} />
                            </div>
                            {selected.message && (
                                <div className="p-4 bg-secondary rounded-2xl">
                                    <p className="em-eyebrow mb-1">Message</p>
                                    <p className="text-sm whitespace-pre-line">{selected.message}</p>
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSelected(null)} className="rounded-full">Fermer</Button>
                        <Button onClick={onDelete} disabled={deleting} variant="destructive" className="rounded-full">
                            {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                            Supprimer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}


function StatCard({ icon: Icon, label, value }) {
    return (
        <Card className="rounded-2xl border-border">
            <CardContent className="p-5 flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl bg-accent flex items-center justify-center"><Icon className="h-6 w-6 text-accent-foreground" /></div>
                <div>
                    <p className="em-eyebrow">{label}</p>
                    <p className="font-display text-3xl font-black text-primary">{value}</p>
                </div>
            </CardContent>
        </Card>
    );
}


function DetailField({ icon: Icon, label, value }) {
    return (
        <div>
            <p className="em-eyebrow flex items-center gap-1.5"><Icon className="h-3 w-3" />{label}</p>
            <p className="text-sm mt-1 break-words">{value || "—"}</p>
        </div>
    );
}
